import { useId, useState } from 'react'
import { Icon } from '../components/Icon'
import { FieldRenderer } from '../components/FieldRenderer'
import { Modal } from '../components/Modal'
import { useReference } from '../data/ReferenceContext'
import type { Ticket, ServiceOffering, FieldDef } from '../domain/types'

export interface TransferTarget { serviceId: number; serviceConfigId: number; sessionId: number }

// Transferir bilhete para outra OFERTA (serviço · fila · sessão) — comportamento #2.
// Coleta também os campos de transição (OnTransfer), quando configurados.
export function TransferModal({ ticket, fields, submitting = false, onClose, onConfirm }: {
  ticket: Ticket
  fields: FieldDef[]
  submitting?: boolean
  onClose: () => void
  onConfirm: (target: TransferTarget, values: Record<string, string>) => void
}) {
  const titleId = useId()
  const { offerings } = useReference()
  const opts = offerings.filter((o) => {
    if (o.serviceConfigId === ticket.serviceConfigId && o.sessionId === ticket.sessionId) {return false}
    if (o.sessionEnd && new Date(o.sessionEnd) < new Date()) {return false}
    return true
  })
  const [sel, setSel] = useState<ServiceOffering | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})
  const fieldsOk = fields.every((f) => !f.required || (values[f.key] || '').trim() !== '')

  return (
    <Modal
      open
      onClose={submitting ? () => {} : onClose}
      titleId={titleId}
      title="Transferir bilhete"
      closeOnOverlay={!submitting}
    >
      <div data-screen-label="Transferir bilhete">
        <div className="fz-modal__icon"><Icon name="arrowLeftRight" size={24} /></div>
        <div className="fz-modal__title" id={titleId}>Transferir bilhete</div>
        <div className="fz-modal__text">
          Transferir <b>{ticket.codigoTicket}</b> ({ticket.nomeServico}) para outra fila/sessão. O bilhete entra na nova fila como aguardando, com novo código.
        </div>
        <div className="fz-field">
          <label className="fz-field__label">Destino (serviço · fila · sessão)</label>
          <div className="fz-transfer__opts">
            {opts.length === 0 && <div className="fz-sessioncal__empty">Nenhuma fila/sessão disponível para destino.</div>}
            {opts.map((o) => (
              <button key={o.serviceConfigId + ':' + o.sessionId + ':' + o.serviceId}
                className={'fz-transfer__opt ' + (sel === o ? 'is-active' : '')} disabled={submitting} onClick={() => setSel(o)}>
                <span><span className="fz-svcchip__code">{o.code}</span> {o.serviceName}</span>
                <span className="fz-transfer__sala">{o.filaNome} · {o.sala}{o.sessionLabel !== 'Dia inteiro' ? ' · ' + o.sessionLabel : ''}</span>
              </button>
            ))}
          </div>
        </div>
        {fields.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, textAlign: 'left' }}>
            {fields.map((f, i) => (
              <FieldRenderer
                key={f.key}
                def={f}
                autoFocus={i === 0 && !!sel}
                value={values[f.key] || ''}
                onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
              />
            ))}
          </div>
        )}
        <div className="fz-modal__actions">
          <button className="fz-btn fz-btn--neutral fz-btn--block" onClick={onClose} disabled={submitting}>Voltar</button>
          <button className="fz-btn fz-btn--primary fz-btn--block" disabled={!sel || !fieldsOk || submitting} aria-busy={submitting}
            onClick={() => sel && !submitting && onConfirm({ serviceId: sel.serviceId, serviceConfigId: sel.serviceConfigId, sessionId: sel.sessionId }, values)}>
            {submitting ? <><span className="fz-spin" /> Transferindo…</> : 'Transferir'}
          </button>
        </div>
      </div>
    </Modal>
  )
}